import type {
  ArrayPath,
  Control,
  FieldArray,
  FieldErrors,
  FieldValues,
  Path,
  UseFormGetValues,
  UseFormRegister
} from 'react-hook-form';
import { useFieldArray } from 'react-hook-form';
import styled, { css } from 'styled-components';
import type { Merge, PlainObject } from 'simplytyped';

import { PrimaryIconButton, SecondaryButton } from '../Buttons';
import type {
  InputArrayPrimitiveType,
  InputWrapperProps
} from './InputWrapper';
import {
  getInput,
  HelpTextWrapper,
  InputLabel,
  RequiredSymbol
} from './InputWrapper';
import { errorToMessageString, Logger } from '../../../common';
import { cloneDeep, isNil, isPlainObject, last } from 'lodash';

export type ArrayInputWrapperProps<Fields extends FieldValues = FieldValues> =
  Merge<
    Omit<InputWrapperProps, 'value' | 'errorMessage' | 'type'>,
    {
      control: Control<Fields>;
      name: ArrayPath<Fields>;
      register: UseFormRegister<Fields>;
      errors: FieldErrors<Fields> | undefined;
      defaultValue?: FieldArray<Fields, ArrayPath<Fields>>;
    }
  > &
    (
      | {
          // defaultValue?: string | number | boolean;
          getValues?: UseFormGetValues<Fields>;
          type: InputWrapperProps['type'];
        }
      | {
          // defaultValue?: PlainObject;
          getValues?: UseFormGetValues<Fields>;
          inputs: Array<InputWrapperProps>; // for an array of objects
        }
    );

const Wrapper = styled.div(
  ({ theme }) => css`
    display: block;
    position: relative;
    margin-bottom: ${theme.margins.md};
  `
);

const AddWrapper = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: row-reverse;
    justify-content: space-between;
    align-items: center;
    margin: ${theme.margins.md} 0;
  `
);

const ErrorWrapper = styled.span(
  ({ theme }) => css`
    display: block;
    color: ${theme.colors.main.error};
    font-size: ${theme.fonts.sizes.h6};
    margin-top: -0.75rem;
  `
);

const ListWrapper = styled.div(
  ({ theme }) => css`
    display: flex;
    position: relative;
    margin-bottom: ${theme.margins.sm};

    > *:first-child {
      flex-grow: 1;
    }

    > *:nth-child(1) {
      flex-shrink: 0;
      flex-basis: 0;
    }
  `
);

const ArrayObjectWrapper = styled.span(
  ({ theme }) => css`
    display: grid;
    grid-gap: ${theme.margins.sm};
    grid-auto-flow: column;
  `
);

const NoItemsWrapper = styled.div(
  ({ theme }) => css`
    display: flex;
    position: relative;
    margin-bottom: ${theme.margins.sm};
    justify-content: center;
    align-items: center;
    padding: 1rem;
    border: 1px solid ${theme.colors.main.accent};
    background-color: ${theme.colors.lightWhiteSmoke};
  `
);

function InputArrayItem<Fields extends FieldValues = FieldValues>({
  name,
  attrs: { inputs, ...attrs }
}: {
  attrs: Omit<ArrayInputWrapperProps, 'defaultValue' | 'name'>;
  name: Path<Fields>;
}) {
  if (inputs) {
    return (
      <ArrayObjectWrapper>
        {inputs.map((input) => {
          return (
            <span key={`${name}.${input.name}`}>
              {input.label && <InputLabel>{input.label}</InputLabel>}
              <InputArrayItem
                attrs={
                  {
                    ...attrs,
                    ...input
                  } as any
                }
                name={`${name}.${input.name}`}
              />
            </span>
          );
        })}
      </ArrayObjectWrapper>
    );
  }

  return (
    <span>
      {getInput({
        // Don't pass value as register will handle it
        ...attrs,
        name,
        type: ('type' in attrs ? attrs.type : '') as InputArrayPrimitiveType
      })}
    </span>
  );
}

export const ArrayInputWrapper = <Schema extends FieldValues = FieldValues>({
  defaultValue,
  ...props
}: ArrayInputWrapperProps<Schema>) => {
  const {
    label,
    required,
    errors,
    helpText,
    control,
    name,
    getValues = (() => null) as any
  } = props;

  const { fields, append, remove } = useFieldArray<Schema>({
    control, // control props comes from useForm
    name, // unique name for your Field Array
    rules: {
      required
    }
  });

  const errorMessage = !Array.isArray(errors)
    ? errorToMessageString(errors)
    : undefined;

  return (
    <Wrapper>
      {label && (
        <InputLabel>
          {label}
          {required && <RequiredSymbol>*</RequiredSymbol>}
        </InputLabel>
      )}
      {helpText && <HelpTextWrapper>{helpText}</HelpTextWrapper>}
      {!fields.length && (
        <NoItemsWrapper>
          <span>There are no items to display in this list</span>
        </NoItemsWrapper>
      )}
      {fields.map((field, index) => {
        let error = Array.isArray(errors) ? errors[index] : undefined;

        if (isPlainObject(error) && 'inputs' in props) {
          error = Object.values(error as Record<string, PlainObject>)
            .flatMap((e) => ('message' in e ? e.message : ''))
            .filter((e) => e)
            .join(', ');
        }

        const errorMsg = errorToMessageString(error);

        if (error) {
          // TODO log
          // Logger.error({
          //   source: 'Input Array',
          //   message: JSON.stringify(error)
          // });
          console.log({ error });
        }

        return (
          <div key={field.id}>
            <ListWrapper key={`${name}.${index}`}>
              <InputArrayItem
                attrs={props as any}
                name={`${name}.${index}` as Path<FieldValues>}
              />
              <span>
                {'inputs' in props && props.inputs?.some((i) => i.label) && (
                  <InputLabel style={{ visibility: 'hidden' }}>
                    Actions
                  </InputLabel>
                )}
                <SecondaryButton
                  onClick={() => remove(index)}
                  type={'button'}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    fontSize: 'inherit',
                    marginLeft: '12px',
                    padding: '9px 12px'
                  }}
                >
                  <span>Remove</span>
                  <span className="material-symbols-rounded">delete</span>
                </SecondaryButton>
              </span>
            </ListWrapper>
            {errorMsg && (
              <ErrorWrapper style={{ marginTop: '-0.75rem' }}>
                {errorMsg}
              </ErrorWrapper>
            )}
          </div>
        );
      })}
      <AddWrapper>
        <PrimaryIconButton
          type={'button'}
          onClick={() => {
            if (isNil(defaultValue)) {
              append(
                cloneDeep(
                  last(getValues(props.name as Path<Schema>))
                ) as unknown as FieldArray<Schema, ArrayPath<Schema>>
              );
            } else {
              append(defaultValue);
            }
          }}
        >
          <span>Add</span>
          <span className="material-symbols-rounded">add</span>
        </PrimaryIconButton>
        {errorMessage && <ErrorWrapper>{errorMessage}</ErrorWrapper>}
      </AddWrapper>
    </Wrapper>
  );
};
